import { MapPin } from "@phosphor-icons/react";
import { Drawer, Field } from "./shared";
import AvmLiteBanner from "../networth/AvmLiteBanner";

/**
 * The Add/Edit property drawer. Form state owned by parent so this stays pure.
 *
 * Behaviour: identical to the previous in-page implementation — only moved.
 * Score weights, status flow (Evaluating / Owned / Sold), city-preset chips,
 * subjective-score grid all preserved.
 */
export default function PropertyFormDrawer({ form, setForm, isEdit, cities, onClose, onSubmit }) {
  return (
    <Drawer onClose={onClose} title={isEdit ? "Edit property" : "Add property"}>
      <form onSubmit={onSubmit} className="space-y-5" data-testid="property-form">
        <Field label="Name" required>
          <input
            className="input-dark w-full px-3 py-2"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            data-testid="form-name"
          />
        </Field>

        {cities.length > 0 && (
          <div className="border hairline bg-[hsl(var(--muted))] p-4">
            <div className="flex items-center gap-2 mb-3 text-[hsl(var(--secondary))]">
              <MapPin size={14} weight="duotone" />
              <span className="eyebrow text-[hsl(var(--secondary))]">Apply city preset</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {cities.map((c) => (
                <button
                  type="button"
                  key={c.city}
                  onClick={() =>
                    setForm({
                      ...form,
                      location: c.city,
                      expected_appreciation: c.expected_appreciation,
                      rental_yield: c.rental_yield,
                    })
                  }
                  className="btn-ghost px-3 py-1.5 text-xs"
                  data-testid={`city-preset-${c.city.replace(/\s+/g, "-")}`}
                  title={c.notes}
                >
                  {c.city} · {c.expected_appreciation}% / {c.rental_yield}%
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Type">
            <select
              className="input-dark w-full px-3 py-2"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              data-testid="form-type"
            >
              <option value="flat">Flat</option>
              <option value="villa">Villa</option>
              <option value="plot">Plot</option>
              <option value="commercial">Commercial</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Location">
            <input
              className="input-dark w-full px-3 py-2"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              data-testid="form-location"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Price (₹)">
            <input
              type="number"
              className="input-dark w-full px-3 py-2"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              data-testid="form-price"
            />
          </Field>
          <Field label="Area (sqft)">
            <input
              type="number"
              className="input-dark w-full px-3 py-2"
              value={form.area_sqft}
              onChange={(e) => setForm({ ...form, area_sqft: e.target.value })}
              data-testid="form-area"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Down payment (₹)">
            <input type="number" className="input-dark w-full px-3 py-2" value={form.down_payment} onChange={(e) => setForm({ ...form, down_payment: e.target.value })} />
          </Field>
          <Field label="Maint. / month">
            <input type="number" className="input-dark w-full px-3 py-2" value={form.maintenance_monthly} onChange={(e) => setForm({ ...form, maintenance_monthly: e.target.value })} />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Loan %">
            <input type="number" step="0.1" className="input-dark w-full px-3 py-2" value={form.loan_rate} onChange={(e) => setForm({ ...form, loan_rate: e.target.value })} />
          </Field>
          <Field label="Tenure (yrs)">
            <input type="number" className="input-dark w-full px-3 py-2" value={form.loan_tenure_years} onChange={(e) => setForm({ ...form, loan_tenure_years: e.target.value })} />
          </Field>
          <Field label="Appr. %">
            <input type="number" step="0.1" className="input-dark w-full px-3 py-2" value={form.expected_appreciation} onChange={(e) => setForm({ ...form, expected_appreciation: e.target.value })} />
          </Field>
        </div>

        <Field label="Rental yield %">
          <input type="number" step="0.1" className="input-dark w-full px-3 py-2" value={form.rental_yield} onChange={(e) => setForm({ ...form, rental_yield: e.target.value })} />
        </Field>

        <div className="border-t hairline pt-5">
          <div className="eyebrow mb-3">Status</div>
          <div className="flex gap-2 mb-4">
            {[
              ["evaluating", "Evaluating"],
              ["owned", "Owned"],
              ["sold", "Sold"],
            ].map(([id, label]) => (
              <button
                type="button"
                key={id}
                onClick={() => setForm({ ...form, status: id })}
                data-testid={`form-status-${id}`}
                className={`px-4 py-2 text-sm border transition ${
                  form.status === id
                    ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white"
                    : "hairline text-muted-foreground hover:text-foreground hover:border-[hsl(var(--secondary))]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {(form.status === "owned" || form.status === "sold") && (
            <div className="space-y-4" data-testid="owned-fields">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Purchase date">
                  <input type="date" className="input-dark w-full px-3 py-2" value={form.purchase_date || ""} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} data-testid="form-purchase-date" />
                </Field>
                <Field label="Purchase price (₹)">
                  <input type="number" className="input-dark w-full px-3 py-2" value={form.purchase_price ?? ""} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="form-purchase-price" />
                </Field>
              </div>

              {form.status === "owned" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Current value (₹)">
                      <input type="number" className="input-dark w-full px-3 py-2" value={form.current_value ?? ""} onChange={(e) => setForm({ ...form, current_value: e.target.value })} data-testid="form-current-value" />
                      <AvmLiteBanner
                        city={(form.location || "").split(",").pop().trim()}
                        propertyType={form.type}
                        cmv={Number(form.current_value) || 0}
                        carpetAreaSqft={Number(form.carpet_area_sqft) || 0}
                      />
                    </Field>
                    <Field label="Outstanding loan (₹)">
                      <input type="number" className="input-dark w-full px-3 py-2" value={form.current_loan_balance ?? ""} onChange={(e) => setForm({ ...form, current_loan_balance: e.target.value })} data-testid="form-current-loan" />
                    </Field>
                  </div>
                  <Field label="Carpet area (sqft) — optional, helps AVM benchmark">
                    <input type="number" className="input-dark w-full px-3 py-2"
                      value={form.carpet_area_sqft ?? ""}
                      onChange={(e) => setForm({ ...form, carpet_area_sqft: e.target.value })}
                      placeholder="e.g. 1200"
                      data-testid="form-carpet-area" />
                  </Field>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={!!form.rented}
                      onChange={(e) => setForm({ ...form, rented: e.target.checked })}
                      className="accent-[hsl(var(--primary))] h-4 w-4"
                      data-testid="form-rented"
                    />
                    <span className="text-sm">Currently rented out</span>
                  </label>
                  {form.rented && (
                    <Field label="Monthly rent income (₹)">
                      <input
                        type="number"
                        className="input-dark w-full px-3 py-2"
                        value={form.monthly_rent_income || 0}
                        onChange={(e) => setForm({ ...form, monthly_rent_income: e.target.value })}
                        data-testid="form-rent-income"
                      />
                    </Field>
                  )}
                </>
              )}

              {form.status === "sold" && (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Sold date">
                    <input type="date" className="input-dark w-full px-3 py-2" value={form.sold_date || ""} onChange={(e) => setForm({ ...form, sold_date: e.target.value })} data-testid="form-sold-date" />
                  </Field>
                  <Field label="Sold price (₹)">
                    <input type="number" className="input-dark w-full px-3 py-2" value={form.sold_price ?? ""} onChange={(e) => setForm({ ...form, sold_price: e.target.value })} data-testid="form-sold-price" />
                  </Field>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t hairline pt-5">
          <div className="eyebrow mb-3">Your subjective scores (0–10)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["score_location", "Location"],
              ["score_amenities", "Amenities"],
              ["score_safety", "Safety"],
              ["score_commute", "Commute"],
              ["score_resale", "Resale"],
            ].map(([k, l]) => (
              <Field key={k} label={l}>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.5"
                  className="input-dark w-full px-3 py-2"
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </Field>
            ))}
          </div>
        </div>

        <Field label="Notes">
          <textarea
            className="input-dark w-full px-3 py-2"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Field>

        <div className="flex gap-3 pt-4">
          <button type="submit" className="btn-primary px-5 py-2.5 text-sm" data-testid="save-property-button">
            {isEdit ? "Save changes" : "Add property"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">
            Cancel
          </button>
        </div>
      </form>
    </Drawer>
  );
}
